import React, { useEffect, useState } from 'react'
import { FaRegStar, FaStar } from 'react-icons/fa'
import FaceAngry from '../../../static/icons/face-angry.svg'
import FaceMeh from '../../../static/icons/face-meh.svg'
import FaceGrinHearts from '../../../static/icons/face-grin-hearts.svg'
import ProfessionalBadge from '../../../static/icons/verified-professional.svg'
import MedalBadge from '../../../static/icons/medal-badge.svg'
import CloseModalMobileIcon from '../../../static/icons/arrow-left-mobile.svg'
import CloseModalIcon from '../../../static/icons/close-modal-icon.svg'
import { Rating } from 'react-simple-star-rating'
import moment from 'moment'
import { PET_TYPES, PET_TEMPERAMENTS, PET_SIZES } from 'cobasi-utils/constants'
import { PET_TYPE } from '../../utils/constants'
import { getServiceIconByName } from '../../utils'
import { assetPrefix } from 'config/env_config'
import { getProfessionalRatings } from '../../services/satisfactionSurveyService'
import Highlight from 'components/highlight'
import Alert from '../alert'
import {
  ProfilePhoto,
  ProfileRow,
  ProfileCol,
  ProfileInfo,
  HighlightWrapper,
  ProfileCertifieds,
  ProfileAbout,
  ProfilePets,
  ProfileServices,
  ProfileRatings,
  ProfileRatingItem,
  GlobalStyle,
  ProfileRatingReadMore,
  RatingsLoader,
  RatingContainer,
  UsernameInfo,
  ClientRatings,
  DateInfo,
  ProfileColDetail,
  IconWrapper,
  ServiceIcon,
  ModalClose,
  ModalCloseButton,
  ProfileColContent
} from './styles'
import { useServiceScheduleContext } from '../../context/serviceScheduleContext'

const ProfessionalProfile = ({ professional, servicesList, partner }) => {
  const [loadingRatings, setLoadingRatings] = useState(false)
  const [allRatingsLoaded, setAllRatingsLoaded] = useState(false)
  const [ratings, setRatings] = useState([])
  const [ratingsCount, setRatingsCount] = useState('')
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 3
  })

  const { functions: schedulingFunctions } = useServiceScheduleContext()

  const {
    handleWarningModalClose
  } = schedulingFunctions

  useEffect(() => {
    const { skip, limit } = pagination
    if (professional) getRatings(professional.id, skip, limit)
  }, [professional, pagination])

  const getRatings = async (professionalId, skip, limit) => {
    setLoadingRatings(true)
    const ratingsList = await getProfessionalRatings({ professionalId, skip, limit })

    if (Array.isArray(ratingsList.list)) {
      ratingsList.list.length
        ? setRatings([...ratings, ...ratingsList.list])
        : setAllRatingsLoaded(true)

      setRatingsCount(ratingsList.count)
    }

    setLoadingRatings(false)
  }

  useEffect(() => {
    if (ratings.length === ratingsCount) {
      setAllRatingsLoaded(true)
    }
  }, [ratings, ratingsCount])

  const handlePagination = () => {
    setPagination({
      ...pagination,
      skip: ratings.length
    })
  }

  const formatName = name => {
    const names = name.split(' ')
    const firstName = names[0]
    const secondName = names[1] ? names[1].substr(0, 1).concat('.') : ''

    name = firstName + ' ' + secondName

    return name
  }

  const fullName = name => {
    if (!name) return ''

    return name.split('CNPJ')[0]
  }

  const petTypeList = Object.values(PET_TYPES).map(type => (
    {
      type: type,
      image: type.toLowerCase().concat('-grey'),
      activeImage: type.toLowerCase()
    }
  ))

  const sizesText = {
    MINI: 'pet mini',
    LITTLE: 'pet pequeno',
    MID: 'pet médio',
    BIG: 'pet grande',
    HUGE: 'pet gigante'
  }

  const petSizeList = Object.values(PET_SIZES).map(size => (
    {
      size: size,
      text: sizesText[size]
    }
  ))

  const petTemperamentList = Object.values(PET_TEMPERAMENTS).map(temperament => (
    {
      temperament: temperament,
      text: temperament === 'TAME' ? 'pet manso' : 'pet bravo'
    }
  ))

  // Total de tamanhos e temperamentos de pets
  const totalSizesAndTemperaments = petSizeList.length + petTemperamentList.length

  // Lista de tamanhos e temperamentos de pets que o profissional atende
  const countSizesAndTemperaments = professional.pet_sizes.length + professional.pet_temperaments.length

  const certificationLength = professional.qualificationAndExperiences?.trim().length
  const certifieds = certificationLength ? professional.qualificationAndExperiences : ''

  const PETS_WITHOUT_ICON = ['FERRET', 'PIGGY_INDIAN']
  const IS_MOBILE = window.screen.width <= 575

  const professionalPartner = {
    FRANCHISING: 'Pet Anjo',
    LEASING: 'SPET'
  }

  return (
    <>
      <ProfileRow>
        <ProfileCol isMobile={IS_MOBILE}>
          <ProfileColContent>
            <ProfilePhoto className={!professional.photo ? 'noPhoto' : ''}>
              {professional.photo
                ? <img src={professional.photo} />
                : <img src={`${assetPrefix}/static/icons/profile.svg`} />}
            </ProfilePhoto>
            <ProfileInfo>
              <h3>{fullName(professional.name)}</h3>
              <h4>{partner.name}</h4>
              <h5>CNPJ: {professional.cnpj}</h5>
              <HighlightWrapper>
                <Highlight type='outline'>
                  <ProfessionalBadge />
                  Profissional parceiro {professionalPartner[professional.category] ?? professionalPartner[professional.mainProfessional.category]}
                </Highlight>
                {professional?.isPremiatedProfessional &&
                  <Highlight type='featured'>
                    <MedalBadge />
                    Melhor do ano
                  </Highlight>}
              </HighlightWrapper>
            </ProfileInfo>
          </ProfileColContent>
          <ModalClose onClick={handleWarningModalClose} type='info'>
            <ModalCloseButton>
              {IS_MOBILE
                ? <CloseModalMobileIcon />
                : (
                  <>
                    Fechar
                    <CloseModalIcon />
                  </>
                )}

            </ModalCloseButton>
          </ModalClose>
        </ProfileCol>
      </ProfileRow>

      <ProfileRow overflow>
        <ProfileColDetail>
          <ProfileCertifieds>
            <img src={`${assetPrefix}/static/icons/certificate.svg`} />
            <h4>Certificados</h4>
            {certificationLength
              ? <p>{certifieds}</p>
              : <p>Este profissional não possui Certificados cadastrados.</p>}
          </ProfileCertifieds>
          <ProfileAbout>
            <h4>Sobre mim</h4>
            {professional.description
              ? <p>{professional.description}</p>
              : <p>Este profissional não possui informações profissionais cadastradas.</p>}
          </ProfileAbout>
        </ProfileColDetail>
        <ProfileColDetail>
          <ProfilePets>
            <h4>Pets que aceito</h4>
            <ul>
              {petTypeList.map(pet => {
                return (
                  <li key={pet.type}>
                    {professional.pet_types.includes(pet.type) ? (
                      <img src={`${assetPrefix}/static/icons/${PETS_WITHOUT_ICON.includes(pet.type) ? 'rabbit' : pet.activeImage}-regular.svg`} />
                    ) : (
                      <img src={`${assetPrefix}/static/icons/${PETS_WITHOUT_ICON.includes(pet.type) ? 'rabbit-grey' : pet.image}.svg`} />
                    )}
                    <span>{PET_TYPE[pet.type]}</span>
                  </li>
                )
              }
              )}
            </ul>
            {totalSizesAndTemperaments > countSizesAndTemperaments && (
              <p>
                <img src={`${assetPrefix}/static/icons/x.svg`} />
                <label>
                  Não atendo: &nbsp;
                  {petSizeList.map(pet =>
                    <span key={pet.size}>
                      {!professional.pet_sizes.includes(pet.size) && pet.text.concat('; ')}
                    </span>
                  )}
                  {petTemperamentList.map(pet =>
                    <span key={pet.temperament}>
                      {!professional.pet_temperaments.includes(pet.temperament) && pet.text.concat('; ')}
                    </span>
                  )}
                </label>
              </p>
            )}
          </ProfilePets>
          <ProfileServices>
            <h4>Serviços que faço</h4>
            <ul>
              {servicesList && servicesList.map(service => (
                <li key={`service-${service.value}`}>
                  <ServiceIcon>
                    <img src={getServiceIconByName(service.name, 'outline')} />
                  </ServiceIcon>
                  <label>{service.name.replace('&', ' & ')}</label>
                </li>
              ))}
            </ul>
          </ProfileServices>
          <ProfileRatings>
            <h4>Avaliações dos clientes de {fullName(professional.name)}</h4>
            {
              professional.ratingAverage
                ? (
                  <ClientRatings>
                    <section>
                      <p>Nota &#8211; <b>{parseFloat(professional.ratingAverage)}</b>/5</p>
                      <p>Total de avaliações &#8211; <b>{ratingsCount}</b></p>
                    </section>
                    {
                      <section>
                        <p>Pontualidade</p>
                        <Rating
                          ratingValue={(professional.punctualityRating * 20)}
                          fillColor='var(--dark-yellow)'
                          allowHalfIcon
                          fullIcon={<FaStar size={14.5} color='var(--dark-yellow)' />}
                          emptyIcon={<FaRegStar size={14.5} color='var(--dark-yellow)' />}
                          readonly={professional.punctualityRating > 0}
                        />
                      </section>
                    }
                    {
                      <section>
                        <p>Qualidade do serviço</p>
                        <Rating
                          ratingValue={(professional.qualityOfServicesProvidedRating * 20)}
                          fillColor='var(--dark-yellow)'
                          allowHalfIcon
                          fullIcon={<FaStar size={14.5} color='var(--dark-yellow)' />}
                          emptyIcon={<FaRegStar size={14.5} color='var(--dark-yellow)' />}
                          readonly={professional.qualityOfServicesProvidedRating > 0}
                        />
                      </section>
                    }
                    {
                      <section>
                        <p>Disponibilidade da agenda </p>
                        <Rating
                          ratingValue={(professional.scheduleAvailabilityRating * 20)}
                          fillColor='var(--dark-yellow)'
                          allowHalfIcon
                          fullIcon={<FaStar size={14.5} color='var(--dark-yellow)' />}
                          emptyIcon={<FaRegStar size={14.5} color='var(--dark-yellow)' />}
                          readonly={professional.scheduleAvailabilityRating > 0}
                        />
                      </section>
                    }
                  </ClientRatings>
                )
                : <Alert show type='info'>Este profissional não possui avaliações.</Alert>
            }
          </ProfileRatings>
          <ProfileRatings>
            <h4>
                Comentários dos clientes de {fullName(professional.name)}
            </h4>
            {
              ratings.length > 0
                ? ratings.map(({ id, ratingAverage, userName, createdAt, professionalComment }) => {
                  return (
                    <ProfileRatingItem key={id}>
                      <IconWrapper>
                        {ratingAverage > 3 ? (
                          <FaceGrinHearts size={32} />
                        ) : ratingAverage < 2 ? (
                          <FaceAngry />
                        ) : (
                          <FaceMeh />
                        )}
                      </IconWrapper>
                      <RatingContainer>
                        <Rating
                          ratingValue={(ratingAverage * 20)}
                          fillColor='var(--dark-yellow)'
                          allowHalfIcon
                          fullIcon={<FaStar size={14.5} color='var(--dark-yellow)' />}
                          emptyIcon={<FaRegStar size={14.5} color='var(--dark-yellow)' />}
                          readonly={ratingAverage > 0}
                        />
                        <UsernameInfo>{formatName(userName)}</UsernameInfo>
                        <DateInfo>{moment(createdAt).locale('pt-br').format('DD [de] MMMM [de] YYYY')}</DateInfo>
                        {professionalComment && <p>“{professionalComment}”</p>}
                      </RatingContainer>
                    </ProfileRatingItem>
                  )
                }
                )
                : <Alert show type='info'>Este profissional não possui comentários.</Alert>
            }
            <ProfileRatingReadMore onClick={handlePagination} disabled={loadingRatings || allRatingsLoaded}>Ler mais comentários</ProfileRatingReadMore>
            {loadingRatings && (
              <RatingsLoader>
                <img src={`${assetPrefix}/static/images/spinner.svg`} />
              </RatingsLoader>
            )}
          </ProfileRatings>
        </ProfileColDetail>
      </ProfileRow>
      <GlobalStyle />
    </>
  )
}

export default ProfessionalProfile
